import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';
import { decodeJwt } from '../utils/jwt';
import type { AuthContextType, User } from '../types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const storedToken = authService.getToken();
    if (storedToken) {
      const payload = decodeJwt(storedToken);
      if (payload) {
        setToken(storedToken);
        setUser({
          userId: payload.sub,
          username: payload.username,
          email: payload.email,
          roles: payload.roles || [],
          isSuperuser: payload.isSuperuser ?? false,
          isStaff: payload.isStaff ?? false,
          isModerator: payload.isModerator ?? false,
        });
      } else {
        // Invalid token, remove it
        authService.logout();
      }
    }
    setIsLoading(false);
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    try {
      const response = await authService.login({ username, password });
      authService.setToken(response.accessToken);

      const payload = decodeJwt(response.accessToken);
      if (!payload) {
        throw new Error('Invalid token received');
      }

      const userData: User = {
        userId: payload.sub,
        username: payload.username,
        email: payload.email,
        roles: payload.roles || [],
        isSuperuser: payload.isSuperuser ?? false,
        isStaff: payload.isStaff ?? false,
        isModerator: payload.isModerator ?? false,
      };

      setToken(response.accessToken);
      setUser(userData);
      authService.setUser(userData);
    } catch (error) {
      throw error;
    }
  }, []);

  const logout = useCallback(() => {
    authService.logout();
    setToken(null);
    setUser(null);
  }, []);

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    isAuthenticated: !!user && !!token,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

